import { format, parse } from "date-fns";
import { useEffect, useState } from "react";
import { generateFullHourOptions } from ".";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import { TDayData } from "../../../store/candidate/types";
import CustomButton from "../../UI/Buttons/CustomButton";
import PlusCircle from "../../UI/Icons/PlusCircle";
import RoundedTick from "../../UI/Icons/RoundedTick";
import ErrorMessage from "../../UI/Tooltips/ErrorMessage";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import { calendarAvailabilitiesThunk } from "../../../store/candidate/thunks";
import { isFulfilled } from "@reduxjs/toolkit";
import Block from "../../UI/Icons/Block";

const formatDayLabel = (dateStr: string, dayKey: string): string => {
  const date = new Date(dateStr);
  const shortDay = dayKey.slice(0, 3);
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });
  return `${shortDay} ${day} ${month}`;
};

interface EditSingleDayProps {
  dayData: TDayData;
  dayKey: string;
  onRefresh: () => void;
  onClickClose: () => void;
  isMobile?: boolean;
  setShowEditSingleAvailability?: (show: boolean) => void;
  setCurrentSelectedDay?: (day: string) => void;
}

type TimeSlot = { start_time: string; end_time: string; error?: string };
const defaultSlot: TimeSlot[] = [{ start_time: "07:00", end_time: "18:00" }];

const EditSingleDay = ({
  dayData,
  dayKey,
  onRefresh,
  onClickClose,
  isMobile,
  setShowEditSingleAvailability,
  setCurrentSelectedDay,
}: EditSingleDayProps) => {
  const dispatch = useDispatch();
  const inProgress = useSelector((state) => state.onboarding.inProgress);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>(
    dayData?.availability.map((slot) => ({
      start_time: slot.start_time?.slice(0, 5) ?? "07:00", // "06:00:00" → "06:00"
      end_time: slot.end_time?.slice(0, 5) ?? "18:00",
    })) || defaultSlot
  );

  const timeOptions = generateFullHourOptions();
  const [originalSlots] = useState(defaultSlot); // store once

  const isChanged = JSON.stringify(originalSlots) !== JSON.stringify(timeSlots);
  const canSubmit = !isDisabled;

  const parseTime = (timeStr: string): number => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const addTimeSlot = () => {
    const newSlot = {
      start_time: "07:00",
      end_time: "18:00",
      error: "",
    };

    const updated = [...timeSlots, newSlot];
    const validationErrors = validateTimeSlots(updated);
    const slotsWithErrors = updated.map((slot, i) => ({
      ...slot,
      error:
        validationErrors[i]?.start_time ||
        validationErrors[i]?.end_time ||
        undefined,
    }));

    setTimeSlots(slotsWithErrors);
    setIsDisabled(validationErrors.some((e) => !!e));
  };

  const removeTimeSlot = (index: number) => {
    const updated = [...timeSlots];
    updated.splice(index, 1);

    const validationErrors = validateTimeSlots(updated);
    const slotsWithErrors = updated.map((slot, i) => ({
      ...slot,
      error:
        validationErrors[i]?.start_time ||
        validationErrors[i]?.end_time ||
        undefined,
    }));

    setTimeSlots(slotsWithErrors);
    setIsDisabled(validationErrors.some((e) => !!e));
  };

  const handleTimeChange = (
    index: number,
    field: "start_time" | "end_time",
    value: string
  ) => {
    const updated = [...timeSlots];
    updated[index][field] = value;
    delete updated[index].error;

    const validationErrors = validateTimeSlots(updated);
    const slotsWithErrors = updated.map((slot, i) => ({
      ...slot,
      error:
        validationErrors[i]?.start_time ||
        validationErrors[i]?.end_time ||
        undefined,
    }));

    setTimeSlots(slotsWithErrors);
    setIsDisabled(validationErrors.some((e) => !!e));
  };

  const mergeTimeRanges = (ranges: { start: number; end: number }[]) => {
    if (!ranges.length) return [];

    ranges.sort((a, b) => a.start - b.start);
    const merged: { start: number; end: number }[] = [ranges[0]];

    for (let i = 1; i < ranges.length; i++) {
      const last = merged[merged.length - 1];
      const current = ranges[i];

      if (current.start <= last.end) {
        last.end = Math.max(last.end, current.end);
      } else {
        merged.push(current);
      }
    }

    return merged;
  };

  const parseTime12hToMinutes = (timeStr: string): number => {
    const date = parse(timeStr, "hh:mm a", new Date());
    return date.getHours() * 60 + date.getMinutes();
  };

  const validateTimeSlots = (slots: TimeSlot[]) => {
    const errors: { start_time?: string; end_time?: string }[] = [];

    for (let i = 0; i < slots.length; i++) {
      const { start_time, end_time } = slots[i];
      const start = parseTime(start_time);
      const end = parseTime(end_time);

      if (!start_time || !end_time) {
        errors[i] = { start_time: "Required", end_time: "Required" };
        continue;
      }

      if (end - start < 120) {
        errors[i] = { start_time: "Minimum 2 hours required" };
        continue;
      }

      for (let j = 0; j < slots.length; j++) {
        if (i === j) continue;
        const otherStart = parseTime(slots[j].start_time);
        const otherEnd = parseTime(slots[j].end_time);

        const overlaps =
          (start >= otherStart && start < otherEnd) ||
          (end > otherStart && end <= otherEnd) ||
          (start <= otherStart && end >= otherEnd);

        if (overlaps) {
          errors[i] = { start_time: "Timeframes must not overlap" };
          break;
        }
      }
    }

    // Validate accepted job coverage
    if (dayData?.accepted_jobs?.length) {
      const availabilityRanges = slots.map((slot) => ({
        start: parseTime(slot.start_time),
        end: parseTime(slot.end_time),
      }));

      const mergedRanges = mergeTimeRanges(availabilityRanges);
      const missingJobs: string[] = [];

      for (const job of dayData.accepted_jobs) {
        const dayDate = format(
          parse(dayData.date, "d MMM yyyy", new Date()),
          "yyyy-MM-dd"
        );

        // if (job.date !== dayDate) continue;

        const jobStart = parseTime12hToMinutes(job.start_time);
        const jobEnd = parseTime12hToMinutes(job.end_time);

        const isCovered = mergedRanges.some(
          (range) => jobStart >= range.start && jobEnd <= range.end
        );

        if (!isCovered) {
          missingJobs.push(
            `${job.start_time}–${job.end_time} (${job.centre_name})`
          );
        }
      }

      if (missingJobs.length) {
        errors[0] = {
          start_time: `${missingJobs.join(", ")} required availability for job${
            missingJobs.length > 1 ? "s" : ""
          }.`,
        };
      }
    }

    return errors;
  };

  const getFilteredEndTimeOptions = (startTime: string) => {
    return timeOptions.filter((option) => option.key_param > startTime);
  };

  useEffect(() => {
    const validationErrors = validateTimeSlots(timeSlots);
    const slotsWithErrors = timeSlots.map((slot, i) => ({
      ...slot,
      error:
        validationErrors[i]?.start_time ||
        validationErrors[i]?.end_time ||
        undefined,
    }));

    setTimeSlots(slotsWithErrors);
    setIsDisabled(validationErrors.some((e) => !!e));
  }, [timeSlots.length, dayData?.accepted_jobs, dayData?.date]);

  useEffect(() => {
    const initial =
      dayData?.availability.map((slot) => ({
        start_time: slot.start_time?.slice(0, 5) ?? "07:00",
        end_time: slot.end_time?.slice(0, 5) ?? "18:00",
      })) || defaultSlot;

    setTimeSlots(initial);
  }, [dayData]);

  const handleSubmit = async () => {
    if (!dayData?.date || !dayKey) return;
    // Parse "7 Aug 2025" into a Date object
    const parsedDate = parse(dayData.date, "d MMM yyyy", new Date());

    // Format to "YYYY-MM-DD"
    const formattedDate = format(parsedDate, "yyyy-MM-dd");
    const availabilities = {
      [formattedDate]: timeSlots.map((slot) => ({
        is_available: 2,
        day: dayKey,
        start_time: slot.start_time,
        end_time: slot.end_time,
        has_accepted_jobs: dayData?.accepted_count > 0 ? 1 : 0,
      })),
    };

    const payload = {
      type: "single",
      availabilities
    };

    dispatch(showTransparentPreloaderAction());
    const response = await dispatch(calendarAvailabilitiesThunk(payload));
    if (isFulfilled(response)) {
      document.body.classList.remove("modal-open");
      setCurrentSelectedDay && setCurrentSelectedDay("");
      onRefresh();
      onClickClose();
    }
    dispatch(hideTransparentPreloaderAction());
  };

  const handleCloseCancelEditAvailability = () => {
    setShowEditSingleAvailability && setShowEditSingleAvailability(false);
  };

  return (
    <div className="d-flex flex-column gap-20 max-w-650px">
      {isMobile && (
        <div className="fs-16">
          Edit your available timeframe/s for this day:
        </div>
      )}
      {!isMobile && (
        <>
          <div className="fs-30 fw-400 text-black">
            Edit availability for{" "}
            {dayData?.date && dayKey && formatDayLabel(dayData?.date, dayKey)}
          </div>

          <div className="fs-16 fw-400 text-black">
            Set your availabilities to work below. Each timeframe can only be a
            minimum of 2 hour blocks.
          </div>
        </>
      )}

      <div className="d-flex flex-column gap-20">
        <div className="d-flex flex-column gap-10">
          <div className="radius-10px p-10px gap-10 bg-teal-light">
            <div className="d-flex flex-column gap-10 flex-fill">
              {timeSlots.map((slot, index) => (
                <div key={index}>
                  <div className="d-flex align-items-center gap-10">
                    <select
                      value={slot.start_time}
                      onChange={(e) =>
                        handleTimeChange(index, "start_time", e.target.value)
                      }
                      className="form-select"
                    >
                      {timeOptions.map((opt) => (
                        <option key={opt.key_param} value={opt.key_param}>
                          {opt.value_param}
                        </option>
                      ))}
                    </select>

                    <div className="fs-16 fw-400 text-black">-</div>

                    <select
                      value={slot.end_time}
                      onChange={(e) =>
                        handleTimeChange(index, "end_time", e.target.value)
                      }
                      className="form-select"
                    >
                      {getFilteredEndTimeOptions(slot.start_time).map((opt) => (
                        <option key={opt.key_param} value={opt.key_param}>
                          {opt.value_param}
                        </option>
                      ))}
                    </select>

                    {timeSlots.length > 1 && (
                      <svg
                        onClick={() => removeTimeSlot(index)}
                        className="min-w-24px cursor-pointer"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M6.68867 6.31133C7.31703 5.68297 8.33579 5.68297 8.96414 6.31133L12.3773 9.72453L15.7906 6.31133C16.4189 5.68297 17.4377 5.68297 18.066 6.31133C18.6944 6.93968 18.6944 7.95844 18.066 8.5868L14.6528 12L18.066 15.4132C18.6944 16.0416 18.6944 17.0603 18.066 17.6887C17.4377 18.317 16.4189 18.317 15.7906 17.6887L12.3773 14.2755L8.96414 17.6887C8.33579 18.317 7.31703 18.317 6.68867 17.6887C6.06032 17.0603 6.06032 16.0416 6.68867 15.4132L10.1019 12L6.68867 8.5868C6.06032 7.95844 6.06032 6.93968 6.68867 6.31133Z"
                          fill="#000000"
                          stroke="#E0E0E0"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  {slot.error && <ErrorMessage message={slot.error} />}
                </div>
              ))}

              <button
                onClick={addTimeSlot}
                className="add_timeframe_btn_container btn-theme bg-teal-light text-black mobile-w-100 h-auto fs-13"
              >
                <PlusCircle />
                Add Another Timeframe
              </button>
            </div>
          </div>
        </div>

        <div className="d-flex flex-column flex-lg-row gap-10 justify-content-lg-between">
          <CustomButton
            type="submit"
            isLoading={inProgress}
            contentClass="d-flex"
            variant="primary"
            disabled={isDisabled}
            onClick={() => {
              // prepareAvailabilityInputData();
              handleSubmit();
            }}
            className={`btn-theme ${
              !canSubmit
                ? "text-grey-medium bg-off-white"
                : "bg-teal text-black"
            }`}
          >
            <RoundedTick
              color={!canSubmit || inProgress ? "#757575" : "#000000"}
            />
            Save Changes
          </CustomButton>

          {isMobile && (
            <CustomButton
              variant={"primary"}
              type="button"
              className="btn-theme bg-off-white text-black mobile-w-100"
              onClick={handleCloseCancelEditAvailability}
            >
              Cancel
            </CustomButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditSingleDay;
